import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Formik, Form, Field, FormikProps } from 'formik'
import * as Yup from 'yup'
import { NotFound } from 'src/routes'
import SubscriptionList from 'src/endpoints/form/subscription-list'
import { TextField as FormikTextField } from 'src/ui/formik'
import {
  Box,
  Column,
  Row,
  Card,
  CardContent,
  FormRow,
  Button,
  ChevronLeftIcon,
  FormControl,
  FormLabel,
  Stack,
  Checkbox,
  Tooltip,
  Dialog,
} from 'src/ui'
import { useToast } from 'src/utils/toast'
import { FEATURE_GROUPS } from 'src/companies/subscriptions/feature-groups'

const EndpointSchema = Yup.object().shape({
  url: Yup.string().url('Invalid URL').required('Required'),
  token: Yup.string().required('Required'),
})

interface FormProps {
  endpoint?: Endpoint
  endpoints: Endpoint[]
  environments: Environment[]
  companies: ConfiguratorCompany[]
  features: string[]
  saveEndpoint: (
    args: { endpoint: Endpoint },
    options: {
      onSuccess: (
        [_err, { data }]: [any, { data: any }],
        { endpoint }: { endpoint: Endpoint }
      ) => void
      onError: () => any
    }
  ) => Promise<any>
}

interface FormValues {
  url: string
  token: string
  supported_features?: string[]
}

const EndpointForm: React.FC<FormProps> = ({
  endpoint,
  environments,
  companies,
  features,
  saveEndpoint,
}) => {
  const navigate = useNavigate()
  let toast = useToast()
  const queryClient = useQueryClient()
  let [isLoading, setIsLoading] = useState(false)
  let [pendingValues, setPendingValues] = useState<FormValues | null>(null)

  const initialSupportedFeatures = endpoint.id
    ? (endpoint.supported_features ?? features)
    : (endpoint.supported_features ?? [])

  // Identify features that are part of a group to exclude from top-level list
  const groupedFeaturesSet = new Set<string>()
  Object.values(FEATURE_GROUPS).forEach((groupFeatures) => {
    groupFeatures.forEach((f) => groupedFeaturesSet.add(f))
  })

  // Standalone features: those that are not in any group
  const standaloneFeatures = features.filter((f) => !groupedFeaturesSet.has(f)).sort()

  // Groups
  const groups = Object.keys(FEATURE_GROUPS)

  // Calculate active subscription info per feature
  const featureSubscriptionInfo = React.useMemo(() => {
    const info: Record<string, { count: number; slugs: string[] }> = {}

    const processSubscription = (sub: { features: string[]; company_slug: string }) => {
      sub.features.forEach((feature) => {
        if (!info[feature]) {
          info[feature] = { count: 0, slugs: [] }
        }
        info[feature].count += 1
        info[feature].slugs.push(sub.company_slug)
      })
    }

    endpoint.subscriptions?.forEach(processSubscription)
    return info
  }, [endpoint.subscriptions])

  const performSave = async (values: FormValues) => {
    setIsLoading(true)
    const submissionValues = {
      ...values,
      supported_features: values.supported_features,
    }

    await saveEndpoint(
      { endpoint: submissionValues as Endpoint },
      {
        onSuccess: async ([_err, { data }], { endpoint }) => {
          await queryClient.invalidateQueries({ queryKey: ['endpoints'] })
          toast({
            description: `Successfully ${endpoint.id ? 'updated' : 'created'} endpoint`,
          })
          setIsLoading(false)
          navigate(`/translators/endpoints/${endpoint.id ? endpoint.id : data.data.id}/edit`)
        },
        onError: () => {
          toast({ status: 'error', description: 'Failed to save endpoint' })
          setIsLoading(false)
        },
      }
    )
  }

  const removedWithSubscriptions = pendingValues
    ? initialSupportedFeatures
        .filter((f) => !pendingValues.supported_features?.includes(f))
        .filter((f) => (featureSubscriptionInfo[f]?.count || 0) > 0)
    : []

  return (
    <>
      <Row alignItems="center" mb={4}>
        <Box>
          <Button
            size="sm"
            leftIcon={<ChevronLeftIcon />}
            onClick={() => navigate('/translators/endpoints')}
          >
            Back to List
          </Button>
        </Box>
      </Row>

      <Card mb={3}>
        <CardContent>
          <Column>
            <Row alignItems="center" fontWeight="bold" mb={4}>
              <Box ml={2}>Endpoint</Box>
            </Row>
            <Row alignItems="center" mb={4}>
              <Box ml={2}>Note: new endpoints redact data by default</Box>
            </Row>

            <Formik
              initialValues={{ ...endpoint, supported_features: initialSupportedFeatures }}
              validationSchema={EndpointSchema}
              onSubmit={async (values) => {
                // Check for removal of features with active subscriptions
                const originalFeatures = initialSupportedFeatures
                const newFeatures = values.supported_features || []
                const removedFeatures = originalFeatures.filter((f) => !newFeatures.includes(f))
                const removedWithSub = removedFeatures.filter(
                  (f) => (featureSubscriptionInfo[f]?.count || 0) > 0
                )

                if (removedWithSub.length > 0) {
                  setPendingValues(values)
                } else {
                  await performSave(values)
                }
              }}
            >
              {(formikProps: FormikProps<FormValues>) => (
                <Form>
                  <FormRow>
                    <Field name="url" label="URL" component={FormikTextField} />
                  </FormRow>

                  <FormRow>
                    <Field name="token" label="Token" component={FormikTextField} />
                  </FormRow>

                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.500">
                      Supported Features
                    </FormLabel>
                    <Stack spacing={2} shouldWrapChildren direction="column" mb="6">
                      {/* Render Groups */}
                      {groups.map((groupName) => {
                        // Filter group features to ensure they exist in available features
                        const groupFeatures = FEATURE_GROUPS[groupName].filter((f) =>
                          features.includes(f)
                        )

                        if (groupFeatures.length === 0) return null

                        return (
                          <Box key={groupName} mb={2}>
                            <Box fontWeight="bold" fontSize="sm" mb={1} mt={2}>
                              {groupName}
                            </Box>
                            <Stack spacing={2} pl={4}>
                              {groupFeatures.map((child) => (
                                <FeatureCheckbox
                                  key={child}
                                  feature={child}
                                  subscriptionInfo={featureSubscriptionInfo[child]}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )
                      })}

                      {/* Render Standalone Features */}
                      {standaloneFeatures.map((feature) => (
                        <FeatureCheckbox
                          key={feature}
                          feature={feature}
                          subscriptionInfo={featureSubscriptionInfo[feature]}
                        />
                      ))}
                    </Stack>
                  </FormControl>

                  <Row py={2} justifyContent="flex-end">
                    <Box mx={2}>
                      <Button
                        size="sm"
                        isDisabled={!formikProps.dirty}
                        onClick={() => navigate('/translators/endpoints')}
                      >
                        Cancel
                      </Button>
                    </Box>

                    <Box mx={2}>
                      <Button
                        type="submit"
                        size="sm"
                        colorScheme="primary"
                        isDisabled={!formikProps.dirty}
                        isLoading={isLoading}
                      >
                        Save
                      </Button>
                    </Box>
                  </Row>
                </Form>
              )}
            </Formik>

            {endpoint.id && (
              // do not show subscriptions until the endpoint itself is created
              <SubscriptionList
                endpoint={endpoint}
                companies={companies}
                features={features}
                environments={environments}
                subscriptions={endpoint.subscriptions}
              />
            )}
          </Column>
        </CardContent>
      </Card>

      <Dialog
        title="Confirm Feature Removal"
        isOpen={!!pendingValues}
        onClose={() => setPendingValues(null)}
        actions={
          <>
            <Button size="sm" variant="ghost" mr={2} onClick={() => setPendingValues(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="primary"
              onClick={async () => {
                const values = pendingValues
                setPendingValues(null)
                if (values) await performSave(values)
              }}
            >
              Save and Remove Features
            </Button>
          </>
        }
      >
        <Column>
          <Box mb={4}>The following features have active subscriptions:</Box>
          <Stack spacing={2} mb={4} pl={4}>
            {removedWithSubscriptions.map((f) => (
              <Box key={f} fontSize="sm">
                • <strong>{f}</strong> ({featureSubscriptionInfo[f].count} active subscriptions)
              </Box>
            ))}
          </Stack>
          <Box mb={4}>Existing subscriptions must be manually removed from each company.</Box>
          <Box fontWeight="bold">Are you sure you want to proceed?</Box>
        </Column>
      </Dialog>
    </>
  )
}

interface FeatureCheckboxProps {
  feature: string
  subscriptionInfo?: { count: number; slugs: string[] }
}

const FeatureCheckbox: React.FC<FeatureCheckboxProps> = ({ feature, subscriptionInfo }) => {
  const count = subscriptionInfo?.count || 0
  const slugs = subscriptionInfo?.slugs || []

  const visibleCount = count <= 4 ? 4 : 3
  const visibleSlugs = slugs.slice(0, visibleCount)
  const hiddenCount = count - visibleSlugs.length

  const tooltipLabel =
    count > 0 ? (
      <Box>
        <Box fontWeight="bold" mb={1}>
          Active Subscriptions:
        </Box>
        {visibleSlugs.map((slug) => (
          <Box key={slug}>- {slug}</Box>
        ))}
        {hiddenCount > 0 && <Box mt={1}>...and {hiddenCount} more</Box>}
      </Box>
    ) : null

  return (
    <Field key={feature} name="supported_features">
      {({ field, form }) => (
        <Checkbox
          isChecked={field.value?.includes(feature)}
          onChange={(e) => {
            const currentFeatures = field.value || []
            let nextValue = e.target.checked
              ? [...currentFeatures, feature]
              : currentFeatures.filter((f) => f !== feature)
            form.setFieldValue('supported_features', nextValue)
          }}
        >
          <Row alignItems="center">
            <Box mr={2}>{feature}</Box>
            {count > 0 && (
              <Tooltip label={tooltipLabel} placement="right">
                <Box as="span" color="gray.500" cursor="help">
                  ({count})
                </Box>
              </Tooltip>
            )}
          </Row>
        </Checkbox>
      )}
    </Field>
  )
}

export const NewForm: React.FC<FormProps> = (props) => (
  <EndpointForm
    {...props}
    endpoint={{
      id: null,
      url: '',
      token: '',
      subscriptions: [],
    }}
  />
)

type EditFormProps = FormProps & {
  id: string // from route
}

export const EditForm: React.FC<EditFormProps> = (props) => {
  const { id } = useParams()
  let endpoint = props.endpoints.find((e) => e.id === id)

  if (!endpoint) return <NotFound />

  return <EndpointForm {...props} endpoint={endpoint} />
}
